import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconContext } from "react-icons";
import { IoEyeOffSharp, IoEyeSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ExclamationCircle,
  Exclamation,
  Check,
  Clock,
  LockFill,
  X,
} from "react-bootstrap-icons";
import LogoKemenag from "assets/img/logo-kemenag.png";

// CSS modal rate limit
import "../assets/css/RateLimitModal.css";

const COOLDOWN_MAX_SECONDS = 10;

const pad = (n) => String(n).padStart(2, "0");

export const formatMMSS = (s) =>
  `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;

export const formatHHMMSS = (s) =>
  `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(
    s % 60
  )}`;

function useCountdown(endAt, onFinish) {
  const calc = () => Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
  const [left, setLeft] = useState(calc);

  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  useEffect(() => {
    const timer = setInterval(() => {
      const value = calc();
      setLeft(value);

      if (value <= 0) {
        clearInterval(timer);
        if (finishRef.current) finishRef.current();
      }
    }, 250);

    return () => clearInterval(timer);
  }, [endAt]);

  return left;
}

function WarningContent({ onClose }) {
  return (
    <>
      <div className="rl-icon rl-icon--orange">
        <Exclamation size={44} />
      </div>
      <h5 className="rl-title">Terlalu banyak percobaan login</h5>
      <p className="rl-text">
        Username atau password salah beberapa kali. Jika percobaan
        berikutnya gagal, akun Anda akan masuk ke mode cooldown.
      </p>
      <button type="button" className="rl-btn" onClick={onClose}>
        Tutup
      </button>
    </>
  );
}

function CooldownContent({ endAt, onFinish }) {
  const left = useCountdown(endAt, onFinish);

  return (
    <>
      <div className="rl-icon rl-icon--blue">
        <Clock size={30} />
      </div>
      <h5 className="rl-title">Terlalu banyak percobaan login</h5>
      <p className="rl-text">Silakan tunggu beberapa detik.</p>
      <div className="rl-timer rl-timer--blue">
        <Clock size={34} />
        <div className="rl-timer-body">
          <span className="rl-timer-label">Cooldown</span>
          <span className="rl-timer-value">{formatMMSS(left)}</span>
        </div>
      </div>
    </>
  );
}

function DoneContent({ onClose }) {
  return (
    <>
      <div className="rl-icon rl-icon--green">
        <Check size={44} />
      </div>
      <h5 className="rl-title">Silakan login kembali</h5>
      <p className="rl-text">
        Waktu cooldown telah selesai. <br />
        Anda dapat mencoba login kembali.
      </p>
      <button type="button" className="rl-btn" onClick={onClose}>
        Tutup
      </button>
    </>
  );
}

function BlockContent({ endAt, onClose, onFinish }) {
  const left = useCountdown(endAt, onFinish);

  return (
    <>
      <div className="rl-icon rl-icon--red">
        <LockFill size={26} />
      </div>
      <h5 className="rl-title">Akun Anda diblokir</h5>
      <p className="rl-text">
        Terlalu banyak percobaan login. <br />
        Akun Anda telah diblokir sementara untuk keamanan.
      </p>
      <div className="rl-timer rl-timer--red">
        <Clock size={28} />
        <div className="rl-timer-body">
          <span className="rl-timer-label">Waktu blokir</span>
          <span className="rl-timer-value">{formatHHMMSS(left)}</span>
        </div>
      </div>
      <button type="button" className="rl-btn" onClick={onClose}>
        Tutup
      </button>
    </>
  );
}

function RateLimitModal({ type, endAt, onClose, onFinished }) {
  if (!type) return null;

  return createPortal(
    <div className="rl-overlay" onClick={onClose}>
      <div
        className="rl-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="rl-close"
          onClick={onClose}
          aria-label="Tutup"
        >
          <X size={28} />
        </button>

        {type === "warning" && <WarningContent onClose={onClose} />}

        {type === "cooldown" && (
          <CooldownContent
            endAt={endAt}
            onFinish={() => onFinished && onFinished("cooldown")}
          />
        )}

        {type === "done" && <DoneContent onClose={onClose} />}

        {type === "block" && (
          <BlockContent
            endAt={endAt}
            onClose={onClose}
            onFinish={() => onFinished && onFinished("block")}
          />
        )}
      </div>
    </div>,
    document.body
  );
}

// Toggle switch
function ToggleSwitch({ isChecked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isChecked}
      onClick={onChange}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        isChecked ? "bg-green-600" : "bg-[#eeeeee]"
      }`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${
          isChecked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

// Login component
function Login({ closePopup, onLoginSuccess }) {
  const navigate = useNavigate();

  const popupRef = useRef(null);

  // Remember login
  const rememberedEmail = localStorage.getItem("rememberedEmail") || "";

  const rememberedPassword = localStorage.getItem("rememberedPassword") || "";

  // State
  const [email, setEmail] = useState(rememberedEmail);

  const [password, setPassword] = useState(rememberedPassword);

  const [showPassword, setShowPassword] = useState(false);

  const [isChecked, setIsChecked] = useState(
    !!(rememberedEmail && rememberedPassword)
  );

  const [validationEmail, setValidationEmail] = useState(false);

  const [validationPassword, setValidationPassword] = useState(false);

  // Kode error dari server: 40001 email tidak ditemukan, 40002 password tidak sesuai
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState(null);

  const [lock, setLock] = useState(null);

  // true untuk Login Geschool, false untuk Login Dashboard Admin
  const [useFetch, setUseFetch] = useState(true);

  const emailInvalid = validationEmail || error === 40001;

  const passwordInvalid = validationPassword || error === 40002;

  // Rate limit
  const showRateLimit = (result) => {
    const seconds = Math.max(parseInt(result.retry_after, 10) || 1, 1);

    const type = result.type
      ? result.type
      : seconds > COOLDOWN_MAX_SECONDS
      ? "block"
      : "cooldown";

    setLock({ type, endAt: Date.now() + seconds * 1000 });
    setModal(type);
  };

  const handleModalFinished = (type) => {
    setLock(null);
    setModal(type === "cooldown" ? "done" : null);
  };

  // Klik di luar popup
  const handleOutsideClick = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      closePopup();
    }
  };

  // Remember me
  const handleCheckboxChange = () => {
    const newValue = !isChecked;

    setIsChecked(newValue);

    if (!newValue) {
      localStorage.removeItem("rememberedEmail");

      localStorage.removeItem("rememberedPassword");
    }
  };

  // Handle login
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    setError(null);
    setValidationEmail(false);
    setValidationPassword(false);

    // Validasi
    if (!email && !password) {
      setValidationEmail(true);
      setValidationPassword(true);
      setLoading(false);
      return;
    }

    if (!email) {
      setValidationEmail(true);
      setLoading(false);
      return;
    }

    if (!password) {
      setValidationPassword(true);
      setLoading(false);
      return;
    }

    const data = {
      email,
      password,
      isChecked,
    };

    // Login Geschool
    if (useFetch) {
      fetch("/_api/main/login", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(data),
      })
        .then((res) => {
          return res.json();
        })

        .then((result) => {
          if (result.success) {
            window.location.href = result.redirect_uri;

            return;
          }

          if (result.rate_limited) {
            showRateLimit(result);

            return;
          }

          if (result.error) {
            setError(result.error.code);

            if (result.error.warning) {
              setModal("warning");
            }
          }
        })

        .catch(() => {
          setError(true);
        })

        .finally(() => {
          setLoading(false);
        });
    } else {
      // Login Dashboard Admin
      const loginData = JSON.stringify({
        username: email,
        password: password,
      });

      const config = {
        method: "post",

        maxBodyLength: Infinity,

        url: `/api_w/users/login`,

        headers: {
          "Content-Type": "application/json",

          db_name: process.env.REACT_APP_DB_NAME,
        },

        data: loginData,
      };

      try {
        const response = await axios.request(config);

        if (response.status === 200) {
          const { token, user } = response.data;

          // Simpan login
          localStorage.setItem("token", token);

          localStorage.setItem("id", user.id);

          localStorage.setItem("role", user.role);

          // Remember login
          if (isChecked) {
            localStorage.setItem("rememberedEmail", email);

            localStorage.setItem("rememberedPassword", password);
          } else {
            localStorage.removeItem("rememberedEmail");

            localStorage.removeItem("rememberedPassword");
          }

          toast.success("Berhasil Login");

          // Redirect
          if (user.role === "superadmin" || user.role === "admin") {
            navigate("/admin");
          } else {
            navigate("/");

            if (onLoginSuccess) {
              onLoginSuccess();
            }
          }
        } else {
          toast.error("Gagal Login");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Login UI
  return (
    <>
    <div
      onClick={handleOutsideClick}
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-gray-900
        bg-opacity-50
        px-4
      "
    >
      {/* Form login */}
      <form
        ref={popupRef}
        onSubmit={handleSubmit}
        className="
          text-black
          relative
          flex
          w-[500px]
          max-w-[calc(100vw-32px)]
          flex-col
          rounded-[24px]
          bg-white
          px-6
          py-6
          text-left
          font-light
          shadow-lg
        "
      >
        {/* Logo */}
        <img
          src={LogoKemenag}
          alt="Kemenag Logo"
          className="
            mx-auto
            mb-2
            h-24
            w-auto
            object-contain
          "
        />

        {/* Title */}
        <div
          className="
            flex
            w-full
            flex-col
            gap-1
            text-left
          "
        >
          <div
            className="
              text-5xl
              font-bold
              leading-tight
              text-[#000]
            "
          >
            Masuk
          </div>

          <div
            className="
              text-lg
              font-normal
              text-[#000]
            "
          >
            Silakan masuk menggunakan akun Anda
          </div>
        </div>

        <div className="mt-6 w-full text-left">
          <label
            htmlFor="email"
            className="
              mb-2
              block
              w-full
              text-left
              text-base
              font-medium
              text-[#333333]
            "
          >
            Username / Email
          </label>

          <div
            className={`
    relative
    flex
    h-16
    w-full
    items-center
    rounded-lg
    border
    border-[#dedede]
    bg-white
    px-4
    focus-within:ring-2
    focus-within:ring-[#00520A]

    ${emailInvalid ? "ring-2 ring-red-500" : ""}
  `}
          >
            <input
              id="email"
              type="text"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              aria-invalid={emailInvalid}
              placeholder="Masukkan email / username"
              className="
                bg-transparent
                h-full
                w-full
                text-left
                text-base
                text-[#222222]
                outline-none
                placeholder:text-[#b8b8b8]
              "
            />
          </div>

          {validationEmail && (
            <div
              className="
                mt-1
                flex
                items-center
                gap-1
                text-left
                text-sm
                text-red-500
              "
            >
              <ExclamationCircle size={14} /> Email / username tidak boleh
              kosong
            </div>
          )}

          {error === 40001 && (
            <div
              className="
                mt-1
                flex
                items-center
                gap-1
                text-left
                text-sm
                text-red-500
              "
            >
              <ExclamationCircle size={14} /> Email tidak ditemukan
            </div>
          )}
        </div>

        <div className="mt-5 w-full text-left">
          <label
            htmlFor="password"
            className="
              mb-2
              block
              w-full
              text-left
              text-base
              font-medium
              text-[#333333]
            "
          >
            Password
          </label>

          <div
            className={`
    relative
    flex
    h-16
    w-full
    items-center
    rounded-lg
    border
    border-[#dedede]
    bg-white
    px-4
    focus-within:ring-2
    focus-within:ring-[#00520A]

    ${passwordInvalid ? "ring-2 ring-red-500" : ""}
  `}
          >
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              aria-invalid={passwordInvalid}
              placeholder="Masukkan password"
              className="
                bg-transparent
                h-full
                w-full
                text-left
                text-base
                text-[#222222]
                outline-none
                placeholder:text-[#b8b8b8]
              "
            />

            {/* Eye icon */}
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={
                showPassword ? "Sembunyikan password" : "Tampilkan password"
              }
              className="
                absolute
                right-3
                top-1/2
                -translate-y-1/2
                text-[#555555]
              "
            >
              <IconContext.Provider
                value={{
                  size: "21px",
                }}
              >
                {showPassword ? <IoEyeOffSharp /> : <IoEyeSharp />}
              </IconContext.Provider>
            </button>
          </div>

          {validationPassword && (
            <div
              className="
                mt-1
                flex
                items-center
                gap-1
                text-left
                text-sm
                text-red-500
              "
            >
              <ExclamationCircle size={14} /> Password tidak boleh kosong
            </div>
          )}

          {error === 40002 && (
            <div
              className="
                mt-1
                flex
                items-center
                gap-1
                text-left
                text-sm
                text-red-500
              "
            >
              <ExclamationCircle size={14} /> Password tidak sesuai
            </div>
          )}
        </div>

        <div
          className="
            mt-5
            flex
            items-center
            justify-start
            text-left
          "
        >
          <div
            className="
              flex
              items-center
              gap-3
            "
          >
            <ToggleSwitch
              isChecked={isChecked}
              onChange={handleCheckboxChange}
            />

            <div
              className="
                text-left
                text-base
                text-[#000000]
              "
            >
              Ingat Saya
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="
            mt-8
            flex
            h-14
            w-full
            items-center
            justify-center
            rounded-lg
            bg-[#00520A]
            text-xl
            font-semibold
            text-white
            transition
            hover:bg-[#004408]
            disabled:cursor-not-allowed
            disabled:opacity-60
          "
        >
          {loading ? "Loading..." : "Masuk"}
        </button>

        <div
          className="
            mt-6
            text-center
            text-base
            leading-[1.4]
            text-[#131313]
          "
        >
          Tidak memiliki akun?
          <br />
          Silakan hubungi Admin Madrasah
        </div>
        <div
          className="
            mt-3
            text-center
          "
        >
          <button
            type="button"
            onClick={() => {
              setUseFetch(!useFetch);
              setError(null);
              setValidationEmail(false);
              setValidationPassword(false);
            }}
            className="
              text-green-700
            "
          >
            {useFetch
              ? "Switch to Login Dashboard"
              : "Switch to Login Geschool"}
          </button>
        </div>
      </form>
    </div>
    <RateLimitModal
      type={modal}
      endAt={lock ? lock.endAt : null}
      onClose={() => setModal(null)}
      onFinished={handleModalFinished}
    />
    </>
  );
}

export default Login;